<template>
  <div class="fs-border">
    <label class="switch">
      <input type="checkbox" v-model="enabled">
      <span :style="'#516066'"></span>
    </label>
    <div class="fs-text-size">
      <span class="fs-label mi-icon-rotate">
        <span class="mi">line_weight</span>
      </span>
      <div class="fs-form fs-select">
        <select v-model="settingsModel.width" @change="onChange">
          <option v-for="number in numbersSequence(100, 5)" :key="number" v-html="number" />
        </select>
      </div>
    </div>
    <div class="fs-menu-separator"></div>
    <div class="fs-text-size">
      <div class="fs-form fs-input-text">
        <color-picker :icon="'border_color'" :isBorder="true" v-model="settingsModel.color" @input="onChange" />
      </div>
    </div>
    <div class="fs-menu-separator"></div>
    <div class="fs-text-size">
      <span class="fs-label icon-opacity">
        <span class="icon">
          <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12.7866" cy="13.4338" r="7.18487" stroke="#516066" stroke-width="2.05714" />
            <circle cx="8.52765" cy="8.56641" r="7.18487" stroke="#516066" stroke-width="2.05714" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M16.2486 6.03027C15.2099 5.55148 14.0534 5.28442 12.8345 5.28442C8.32488 5.28442 4.6691 8.9402 4.6691 13.4498C4.6691 14.3294 4.80816 15.1764 5.0655 15.9702C2.26089 14.6775 0.314209 11.8414 0.314209 8.55065C0.314209 4.04102 3.96999 0.385239 8.47962 0.385239C12.1097 0.385239 15.1865 2.75407 16.2486 6.03027Z" fill="#516066" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.43094 5.79856C9.77715 5.66913 10.1345 5.56252 10.5011 5.48057L15.777 10.834C15.6748 11.1858 15.5495 11.5278 15.4029 11.8583L9.43094 5.79856ZM7.23035 7.04014C7.4915 6.8337 7.76557 6.64287 8.05115 6.46908L14.6634 13.1785C14.475 13.4518 14.2704 13.7131 14.0509 13.961L7.23035 7.04014ZM5.56179 8.82157C5.75017 8.54823 5.95477 8.28693 6.1742 8.03907L12.9948 14.9599C12.7336 15.1663 12.4595 15.3571 12.174 15.5309L5.56179 8.82157ZM4.44817 11.1661C4.55043 10.8143 4.67572 10.4723 4.82231 10.1418L10.7941 16.2014C10.4479 16.3308 10.0906 16.4374 9.72391 16.5194L4.44817 11.1661ZM4.17524 14.3637C4.1418 14.0635 4.12463 13.7585 4.12463 13.4494C4.12463 13.2733 4.13021 13.0985 4.14119 12.9252L7.87677 16.7157C7.35118 16.712 6.8374 16.6587 6.33988 16.5601L4.17524 14.3637ZM16.0841 9.07495L12.3482 5.28421C12.8738 5.28788 13.3876 5.3412 13.8851 5.43971L16.0501 7.63653C16.0835 7.93657 16.1006 8.24152 16.1006 8.55048C16.1006 8.72666 16.0951 8.90154 16.0841 9.07495Z" fill="#516066" />
          </svg>
        </span>
      </span>
      <div class="fs-form fs-select">
        <select v-model="settingsModel.opacity" @change="onChange">
          <option v-for="item in numbersSequence(100, 5)" :key="item" :value="item">
            {{ `${item}%` }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Getter } from 'vuex-class';
import { Component, Inject } from 'vue-property-decorator';

import { FsCanvas, BorderSettings, CANVAS_EVENTS } from '@filestack/canvas-sdk';
import ColorPicker from '@/components/ColorPicker.vue';
import { Chrome } from 'vue-color';
import { numbersSequence } from '@/helpers/array';

@Component({
  components: {
    ColorPicker,
    color: Chrome,
  },
})
class Border extends Vue {
  @Inject('canvas')
  private canvas: FsCanvas;

  @Getter('config')
  private config: any;

  private undoHandler: any;

  private settingsModel: BorderSettings = {
    color: '#000',
    width: 20,
    opacity: 50,
  };

  protected get numbersSequence() {
    return numbersSequence;
  }

  protected get enabled() {
    return this.canvas.border.isEnabled;
  }

  protected set enabled(value: boolean) {
    if (!this.canvas.border.isEnabled && value) {
      this.canvas.border.enable({ ...this.settingsModel });
      this.canvas.saveState('borderSettingsUpdate');
      return;
    }

    this.canvas.border.remove();
    this.canvas.saveState('borderSettingsUpdate');
  }

  private get defaultBorderSettings(): BorderSettings {
    return {
      ...this.settingsModel,
      ...this.config('editor.border.defaults'),
    };
  }

  protected mounted() {
    this.undoHandler = () => this.restoreSettings();
    if (this.canvas.border.isEnabled) {
      this.restoreSettings();
    } else {
      this.settingsModel = this.defaultBorderSettings;
    }

    this.canvas.on(CANVAS_EVENTS.UNDO, this.undoHandler);
  }

  protected destroyed() {
    this.canvas.off(CANVAS_EVENTS.UNDO, this.undoHandler);
  }

  private restoreSettings() {
    this.settingsModel = {
      ...this.config('editor.border.defaults'),
      ...this.canvas.border.getSettings(),
    };
  }

  private onChange() {
    this.canvas.border.setSettings({...this.settingsModel});
    this.canvas.saveState('borderSettingsUpdate');
  }
}

export default Border;
</script>
